<template>
  <div class="oauth-callback-view">
    <div class="callback-container">
      <div v-if="isProcessing" class="processing-state">
        <div class="spinner"></div>
        <h2>Completing sign in...</h2>
        <p>Please wait while we complete your authentication.</p>
      </div>

      <div v-if="error" class="error-state">
        <div class="error-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2>{{ error.includes('Access Denied') ? 'Access Denied' : 'Authentication Failed' }}</h2>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button @click="redirectToLogin" class="retry-button">Return to Login</button>
          <button v-if="error.includes('Access Denied')" @click="signOut" class="signout-button">
            Sign Out
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { exchangeCodeForTokens } from '../../services/oauth.service';
import { extractUserFromIdToken, decodeJWT } from '../../utils/jwt-decoder';
import { useAuthStore } from '../../stores/auth.store';
import { signOut as cognitoSignOut } from '../../services/oauth.service';
import { consumeAuthRedirectPath } from '../../services/auth-redirect.service';

const router = useRouter();
const authStore = useAuthStore();
const { isAuthenticated, isTokenValid } = storeToRefs(authStore);

const isProcessing = ref(true);
const error = ref<string | null>(null);

/**
 * Handle OAuth callback
 */
async function handleCallback() {
  try {
    // Log full URL and parameters for debugging
    console.log('[OAuthCallback] Callback received:', {
      fullUrl: window.location.href,
      search: window.location.search,
      hash: window.location.hash,
    });

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    const state = urlParams.get('state');

    // Log all URL parameters
    console.log('[OAuthCallback] URL parameters:', {
      code: code ? `${code.substring(0, 20)}...` : 'MISSING',
      error: errorParam || 'none',
      errorDescription: errorDescription || 'none',
      state: state || 'none',
      allParams: Object.fromEntries(urlParams.entries()),
    });

    if (errorParam) {
      const errorMsg = errorDescription || errorParam || 'Authentication failed';
      console.error('[OAuthCallback] Cognito returned error:', {
        error: errorParam,
        description: errorDescription,
        fullUrl: window.location.href,
      });
      throw new Error(errorMsg);
    }

    if (!code) {
      console.error('[OAuthCallback] No authorization code in URL:', {
        search: window.location.search,
        hash: window.location.hash,
        fullUrl: window.location.href,
      });
      throw new Error(
        'No authorization code received. Cognito may have redirected without a code.',
      );
    }

    console.log('[OAuthCallback] Exchanging code for tokens...');
    const tokenResponse = await exchangeCodeForTokens(code);
    console.log('[OAuthCallback] Token exchange successful');

    const tokenData = {
      accessToken: tokenResponse.access_token,
      refreshToken: tokenResponse.refresh_token || null, // Optional - Azure AD SPA apps may not return refresh_token
      idToken: tokenResponse.id_token,
      expiresIn: tokenResponse.expires_in,
      tokenType: tokenResponse.token_type || 'Bearer',
    };

    authStore.setTokens(tokenData);

    // Use nextTick to ensure reactivity has updated
    await nextTick();

    // Small delay to ensure store state is fully updated
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Manually verify token validity logic as fallback
    const { getTokens, isTokenExpired } = await import('../../services/token-storage.service.js');
    const manualTokenCheck = getTokens();
    const manualExpiredCheck = isTokenExpired();
    const manualIsValid = manualTokenCheck !== null && !manualExpiredCheck;

    const userInfo = extractUserFromIdToken(tokenResponse.id_token);
    if (userInfo) {
      // Debug: Log token contents to help diagnose group issues
      const decodedToken = decodeJWT(tokenResponse.id_token);
      console.log('OAuthCallback: Decoded token claims:', {
        groups: decodedToken?.['cognito:groups'],
        allClaims: Object.keys(decodedToken || {}),
        email: userInfo.email,
      });

      // Check if user has access (has at least one group)
      // Required groups for VCS/rapid-audit access:
      // - Admin, Director, Scientist, oz-vsc-qa1, or the O365 provider group
      // Group format: {UserPoolId}_{ProviderName}
      const requiredGroups = [
        'Admin',
        'Director',
        'Scientist',
        'oz-vsc-qa1',
        'us-east-2_Wjb71lUIf_ozgenellc-ozvcs-O365-qa1', // QA1 group
        'us-east-2_ZoaQbHNNG_ozgenellc-ozvcs-O365-prod', // Production group
      ];

      const userGroups = userInfo.groups || [];
      console.log('OAuthCallback: User groups from token:', userGroups);
      console.log('OAuthCallback: Required groups:', requiredGroups);

      // Check if user has required groups
      const hasRequiredGroup =
        userGroups.length > 0 && userGroups.some((group) => requiredGroups.includes(group));

      // TEMPORARY: If user successfully authenticated via O365 but groups aren't in token,
      // allow access. This handles the case where Cognito groups aren't configured to
      // be included in the ID token. Groups are assigned in Cognito but may not be in token.
      // TODO: Configure Cognito app client to include groups in ID token
      const isO365Authenticated = userInfo.email && userInfo.email.includes('@ozgene.com');
      const hasAccess = hasRequiredGroup || isO365Authenticated;

      if (!hasAccess) {
        // User doesn't have access - clear tokens and show error
        console.error('OAuthCallback: Access denied', {
          userGroups,
          requiredGroups,
          email: userInfo.email,
        });
        authStore.clearAuth();
        throw new Error(
          'Access Denied: Your account does not have permission to access this application. ' +
            'Please contact your administrator to request access.',
        );
      }

      console.log('OAuthCallback: Access granted', {
        hasRequiredGroup,
        isO365Authenticated,
        userGroups,
      });

      authStore.setUser({
        id: userInfo.id,
        email: userInfo.email,
        username: userInfo.username,
        name: userInfo.name,
        emailVerified: true,
        groups: userGroups,
        roles: [],
      });
    } else {
      // Could not extract user info - clear tokens and show error
      authStore.clearAuth();
      throw new Error('Failed to extract user information from authentication token');
    }

    // Small delay to ensure state is fully persisted before redirect
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Final check - use manual validation if store value is still false
    const finalIsTokenValid = isTokenValid.value || manualIsValid;

    if (!isAuthenticated.value || !finalIsTokenValid) {
      console.error('OAuthCallback: Authentication state is invalid after token exchange');
      throw new Error('Authentication state is invalid after token exchange');
    }

    await router.replace(consumeAuthRedirectPath());
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'An unexpected error occurred';
    isProcessing.value = false;
  }
}

/**
 * Redirect to login page
 */
function redirectToLogin() {
  router.push('/login');
}

/**
 * Sign out from Cognito and redirect to login
 */
async function signOut() {
  try {
    await cognitoSignOut();
  } catch (err) {
    console.error('Error signing out:', err);
  } finally {
    authStore.clearAuth();
    router.push('/login');
  }
}

// Handle callback on mount
onMounted(() => {
  handleCallback().catch((err) => {
    error.value = err instanceof Error ? err.message : 'An unexpected error occurred';
    isProcessing.value = false;
  });
});
</script>

<style scoped>
.oauth-callback-view {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
  background-color: #f9fafb;
}

.callback-container {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
}

.processing-state h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 1rem 0 0.5rem;
}

.processing-state p {
  color: #6b7280;
  margin: 0;
}

.error-state h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #dc2626;
  margin: 1rem 0 0.5rem;
}

.error-state p {
  color: #6b7280;
  margin: 0 0 1.5rem;
}

.error-icon {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  color: #dc2626;
}

.spinner {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.retry-button {
  padding: 0.75rem 1.5rem;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background-color: #2563eb;
}

.error-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
}

.signout-button {
  padding: 0.75rem 1.5rem;
  background-color: #6b7280;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.signout-button:hover {
  background-color: #4b5563;
}
</style>
